import {
  Component,
  DestroyRef,
  HostListener,
  effect,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { A11yModule } from '@angular/cdk/a11y';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { IssueDrawerService } from './issue-drawer.service';
import { IssueApiService } from '../../features/issues/issue.service';
import { IssueDetailDto } from '../../features/issues/issue.models';
import { SkeletonComponent } from '../skeleton/skeleton.component';

@Component({
  selector: 'app-issue-drawer',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    A11yModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    SkeletonComponent
  ],
  template: `
    @if (drawer.isOpen()) {
      <div class="issue-drawer-backdrop" (click)="drawer.close()" aria-hidden="true"></div>
      <aside
        class="issue-drawer-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Issue preview"
        cdkTrapFocus
        cdkTrapFocusAutoCapture>
        <header class="issue-drawer-header">
          <div class="issue-drawer-title">
            @if (issue(); as i) {
              <span class="issue-key">{{ i.key }}</span>
            } @else {
              <span class="issue-key">{{ drawer.issueKey() || 'Issue' }}</span>
            }
          </div>
          <div class="issue-drawer-actions">
            @if (resolvedIssueId(); as id) {
              <a mat-stroked-button [routerLink]="['/issues', id]" (click)="drawer.close()">
                <mat-icon>open_in_new</mat-icon>
                Open full page
              </a>
            }
            <button mat-icon-button type="button" (click)="drawer.close()" aria-label="Close">
              <mat-icon>close</mat-icon>
            </button>
          </div>
        </header>

        <div class="issue-drawer-body">
          @if (loading()) {
            <app-skeleton variant="line" [count]="4"></app-skeleton>
          } @else if (error()) {
            <p class="issue-drawer-error">{{ error() }}</p>
          } @else if (issue()) {
            <h2 class="issue-summary">{{ issue()!.summary }}</h2>
            <div class="issue-drawer-meta">
              <span class="tt-badge" [ngClass]="statusBadgeClass(issue()!.status)">{{ issue()!.statusLabel }}</span>
              <span class="tt-badge" [ngClass]="priorityBadgeClass(issue()!.priority)">{{ issue()!.priorityLabel }}</span>
              @if (issue()!.isOverdue) {
                <span class="tt-badge tt-badge--overdue">Overdue</span>
              }
            </div>
            <dl class="issue-drawer-fields">
              <div>
                <dt>Assignee</dt>
                <dd>{{ issue()!.assigneeName || 'Unassigned' }}</dd>
              </div>
              <div>
                <dt>Due date</dt>
                <dd [class.overdue-text]="issue()!.isOverdue">{{ issue()!.dueDate || '—' }}</dd>
              </div>
              @if (issue()!.projectName) {
                <div>
                  <dt>Project</dt>
                  <dd>{{ issue()!.projectName }}</dd>
                </div>
              }
              @if (issue()!.typeLabel) {
                <div>
                  <dt>Type</dt>
                  <dd>{{ issue()!.typeLabel }}</dd>
                </div>
              }
              @if (issue()!.description) {
                <div class="issue-drawer-description">
                  <dt>Description</dt>
                  <dd>{{ issue()!.description }}</dd>
                </div>
              }
            </dl>
          }
        </div>
      </aside>
    }
  `,
  styles: [`
    .issue-drawer-backdrop {
      position: fixed;
      inset: 0;
      z-index: 1000;
      background: color-mix(in srgb, var(--tt-ink) 35%, transparent);
      animation: tt-fade-in 0.2s ease;
    }

    .issue-drawer-panel {
      position: fixed;
      top: 0;
      right: 0;
      z-index: 1001;
      width: min(420px, 100vw);
      height: 100vh;
      display: flex;
      flex-direction: column;
      background: var(--tt-surface);
      border-left: 1px solid var(--tt-border);
      box-shadow: var(--tt-shadow);
      animation: tt-slide-in-right 0.24s ease;
    }

    .issue-drawer-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 14px 16px;
      border-bottom: 1px solid var(--tt-border);
      flex-shrink: 0;
    }

    .issue-drawer-title .issue-key {
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      font-size: 0.85rem;
      font-weight: 650;
      color: var(--tt-primary);
    }

    .issue-drawer-actions {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .issue-drawer-body {
      flex: 1;
      overflow: auto;
      padding: 16px 18px 24px;
    }

    .issue-drawer-error {
      color: var(--tt-danger);
      margin: 0;
    }

    .issue-summary {
      margin: 0 0 14px;
      font-size: 1.05rem;
      font-weight: 650;
      line-height: 1.4;
      color: var(--tt-ink);
    }

    .issue-drawer-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 18px;
    }

    .issue-drawer-fields {
      margin: 0;
      display: grid;
      gap: 12px;
    }

    .issue-drawer-fields div {
      display: grid;
      gap: 2px;
    }

    .issue-drawer-fields dt {
      margin: 0;
      font-size: var(--tt-font-size-small);
      font-weight: 650;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--tt-muted);
    }

    .issue-drawer-fields dd {
      margin: 0;
      color: var(--tt-ink);
    }

    .issue-drawer-description dd {
      white-space: pre-wrap;
      line-height: 1.5;
      font-size: var(--tt-font-size-body);
    }

    .overdue-text {
      color: var(--tt-danger);
      font-weight: 600;
    }

    @keyframes tt-slide-in-right {
      from { transform: translateX(100%); opacity: 0.6; }
      to { transform: translateX(0); opacity: 1; }
    }

    @keyframes tt-fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }
  `]
})
export class IssueDrawerComponent {
  readonly drawer = inject(IssueDrawerService);
  private readonly issuesApi = inject(IssueApiService);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly issue = signal<IssueDetailDto | null>(null);

  constructor() {
    effect(() => {
      const open = this.drawer.isOpen();
      const id = this.drawer.issueId();
      const key = this.drawer.issueKey();
      if (!open || (!id && !key)) {
        this.issue.set(null);
        this.error.set(null);
        return;
      }
      if (id) {
        this.loadIssueById(id);
      } else if (key) {
        this.loadIssueByKey(key);
      }
    });
  }

  resolvedIssueId(): string | null {
    return this.issue()?.id ?? this.drawer.issueId();
  }

  statusBadgeClass(status: number): string {
    const map: Record<number, string> = {
      0: 'tt-badge--status-open',
      1: 'tt-badge--status-progress',
      2: 'tt-badge--status-review',
      3: 'tt-badge--status-done',
      5: 'tt-badge--status-blocked'
    };
    return map[status] ?? 'tt-badge--status-neutral';
  }

  priorityBadgeClass(priority: number): string {
    const map: Record<number, string> = {
      0: 'tt-badge--priority-lowest',
      1: 'tt-badge--priority-low',
      2: 'tt-badge--priority-medium',
      3: 'tt-badge--priority-high',
      4: 'tt-badge--priority-highest'
    };
    return map[priority] ?? 'tt-badge--priority-medium';
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.drawer.isOpen()) {
      this.drawer.close();
    }
  }

  private loadIssueById(id: string): void {
    this.loading.set(true);
    this.error.set(null);
    this.issue.set(null);
    this.issuesApi.getById(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (detail) => {
        this.issue.set(detail);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to load issue.');
        this.loading.set(false);
      }
    });
  }

  private loadIssueByKey(key: string): void {
    this.loading.set(true);
    this.error.set(null);
    this.issue.set(null);
    this.issuesApi.getByKey(key).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (detail) => {
        this.issue.set(detail);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to load issue.');
        this.loading.set(false);
      }
    });
  }
}
